import React, { useState, useEffect } from "react";
import { Avatar } from "primereact/avatar";
import { Skeleton } from "primereact/skeleton";

import { User } from "../../../contexts/User/types";
import { useThemeSwitch } from "../../../../theme/useThemeSwitch";
import { createImageFromInitials } from "./imageHelpers";

interface NavUserImageProps {
  user?: User;
  name?: string;
  gravatarId?: string | null;
  thumbImageUrl?: string | null;
  size?: "normal" | "large" | "xlarge";
  customSize?: string;
  isLoading?: boolean;
}

export const NavUserImage: React.FC<NavUserImageProps> = ({
  user,
  name = "",
  thumbImageUrl = null,
  size = "normal",
  customSize,
  isLoading,
}) => {
  const { appliedTheme } = useThemeSwitch();
  const [src, setSrc] = useState<string | undefined>(undefined);
  const fullName = name || `${user?.name?.givenName || ""} ${user?.name?.familyName || ""}`;

  // Attempt to load a user's profile picture in priority order: '
  // 1) Use smallImageUrl if available
  // 2) Attempt to load Gravatar
  // 3) Fallback to generate initials
  const loadUserImage = async (): Promise<void> => {
    // 1. Use small image URL if available
    if (user?.railsProfile?.smallImageUrl) {
      setSrc(user.railsProfile.smallImageUrl);
      return;
    }

    // 1b. Use thumb image URL if available ()
    if (thumbImageUrl) {
      setSrc(thumbImageUrl);
      return;
    }

    // 2. Fallback to generate initials
    if (!isLoading) {
      setSrc(createImageFromInitials(500, fullName, appliedTheme || "atlas-light"));
    }
  };

  useEffect(() => {
    loadUserImage();
  }, [user, thumbImageUrl, appliedTheme]);

  const avatarStyle = customSize ? { width: customSize, height: customSize } : {};

  if (isLoading)
    return (
      <div style={{ borderRadius: "50%", overflow: "hidden" }}>
        <Skeleton
          size={customSize || (size === "normal" ? "40px" : size === "large" ? "60px" : "80px")}
        />
      </div>
    );

  return <Avatar image={src} size={size} shape="circle" style={avatarStyle} />;
};
